import { Suspense, useContext, useEffect, useState } from "react";
import "./App.scss";
import { GeneralAppContext } from "./General/GeneralAppProvider";
import AppHeader from "./AppHeader/AppHeader";
import { Redirect, Switch, useHistory } from "react-router-dom";
import { useFlags } from "launchdarkly-react-client-sdk";
import ProtectedRoute from "./AppRouter/ProtectedRoute";
import RedirectPreservingParams from "./AppRouter/RedirectPreservingParams";
import CustomerProvisioning from "./CustomerProvisioning/CustomerProvisioning";
import {
  AppPreferencesContext,
  userActionsEnum,
} from "./General/AppPreferencesProvider";
import useTermsAndConditions from "./TermsAndConditions/useTermsAndConditions";
import TermsAndConditions from "./TermsAndConditions/TermsAndConditions";
import { AuthContext } from "./Auth/AuthContext";
import lazyComponentLoader from "./ComponentLoader";
import StaticErrorFallback from "./Components/ErrorFallback/StaticErrorFallback";
import { DatasourcesProvider } from "./General/DatasourcesProvider";
import DomainAlert from "./General/DomainAlert/DomainAlert";
import { openFreshchat } from "./ThirdPartyProviders/ThirdPartyProviders";
import {
  Tooltip,
  MODAL_ROOT_ID,
  TextButton,
  IconButton,
  ModalContext,
  ToastRoot,
  PageNotFound,
} from "@SeemplicityDev/shared";
import EndOfYear from "./EndOfYear/EndOfYear";
import useDisplayEOY from "./EndOfYear/useDisplayEOY";
import ChatStreamProvider from "./SeemChat/Provider/ChatStreamProvider";
import { SILVER_RESOURCE_BASE_PATH } from "./Resources/ResourceDetailsPane/SilverResources/silverResourceLinkUtils";
import {
  ANALYST_AUTOMATION_WIZARD_CREATE_PATH,
  ANALYST_AUTOMATION_WIZARD_EDIT_PATH,
  ANALYST_DETAILS_PANE_PATH,
  ANALYST_RUNS_FINDING_MEMBER_PATH,
  ANALYST_RUNS_FINDING_PATH,
  ANALYST_RUNS_PATH,
} from "./AnalystRuns/analystRunsRoutes";
import { useAnalystApiEnabled } from "./AnalystRuns/AnalystRunsApi";
import useRemediationItemsSplit from "./General/useRemediationItemsSplit";
import {
  FINDINGS_PATH,
  REMEDIATION_ITEMS_PATH,
  SEEMPLICITY_FINDINGS_PATH,
} from "./Findings/findingEntity";

const FindingsProvider = lazyComponentLoader(
  () => import("./Findings/FindingsProvider")
);

const RemediationItemsProvider = lazyComponentLoader(
  () => import("./Findings/RemediationItemsProvider")
);

const DataValidationFindingsProvider = lazyComponentLoader(
  () =>
    import("./Findings/DataValidationFindings/DataValidationFindingsProvider")
);

const SeemplicityFindingsProvider = lazyComponentLoader(
  () => import("./SeemplicityFindings/SeemplicityFindingsProvider")
);

const AutomationsRouter = lazyComponentLoader(
  () => import("./Remediation/AutomationsRouter")
);

const Rules = lazyComponentLoader(() => import("./Rules/Rules"));

const SettingsMenu = lazyComponentLoader(
  () => import("./AppSettings/SettingsMenu/SettingsMenu")
);
const ResourcesProvider = lazyComponentLoader(
  () => import("./Resources/ResourcesProvider")
);
const SilverResourceDetailsPaneProvider = lazyComponentLoader(
  () =>
    import(
      "./Resources/ResourceDetailsPane/SilverResources/SilverResourceDetailsPaneProvider"
    )
);
const DashboardProvider = lazyComponentLoader(
  () => import("./CustomizedDashboard/DashboardProvider")
);

const SystemHistory = lazyComponentLoader(
  () => import("./SystemHistory/SystemHistory")
);

const ReportsAuditLog = lazyComponentLoader(
  () => import("./ReportsAuditLog/ReportsAuditLog")
);

const ScopesGeneratorPage = lazyComponentLoader(
  () => import("./ScopesGenerator/ScopesGeneratorPage")
);

const ScopesGeneratorPageProvider = lazyComponentLoader(
  () => import("./ScopesGenerator/ScopesGeneratorPageProvider")
);

const AggregationRulesPage = lazyComponentLoader(
  () => import("./AggregationRules/AggregationRules")
);

const TicketSLARulesPage = lazyComponentLoader(
  () => import("./TicketSlaRules/TicketSLARulesWrapper")
);

const ChatProvider = lazyComponentLoader(
  () => import("./SeemChat/Provider/ChatProvider")
);

const AnalystRuns = lazyComponentLoader(
  () => import("./AnalystRuns/AnalystRuns")
);

const AnalystAutomationWizard = lazyComponentLoader(
  () => import("./AnalystRuns/AnalystAutomationWizard/AnalystAutomationWizard")
);

const ANALYST_AUTOMATION_WIZARD_PATHS = [
  ANALYST_AUTOMATION_WIZARD_EDIT_PATH,
  ANALYST_AUTOMATION_WIZARD_CREATE_PATH,
];

const ANALYST_RUNS_PATHS = [
  ANALYST_RUNS_FINDING_MEMBER_PATH,
  ANALYST_RUNS_FINDING_PATH,
  ANALYST_DETAILS_PANE_PATH,
  ANALYST_RUNS_PATH,
];

const FINDINGS_MEMBER_ROUTE_PATH = `${FINDINGS_PATH}/:id/member`;
const REMEDIATION_ITEMS_MEMBER_ROUTE_PATH = `${REMEDIATION_ITEMS_PATH}/:id/member`;
const FINDINGS_ROUTE_PATH = `${FINDINGS_PATH}/:id?/:state?`;
const REMEDIATION_ITEMS_ROUTE_PATH = `${REMEDIATION_ITEMS_PATH}/:id?/:state?`;
const SEEMPLICITY_FINDINGS_ROUTE_PATH = `${SEEMPLICITY_FINDINGS_PATH}/:id?`;
const SEEMPLICITY_FINDING_DETAILS_ROUTE_PATH = `${SEEMPLICITY_FINDINGS_PATH}/:id`;

function App() {
  const {
    customerProvisioning,
    termsAndConditions,
    showDomainLocation,
    hideAggregationRulesPage,
  } = useFlags();

  const isAutomationsEnabled = useAnalystApiEnabled();
  const isRemediationItemsSplit = useRemediationItemsSplit();

  const {
    appPreferences: { hasScopesAllowed, useClickhouse, useAI },
    isAllowed,
  } = useContext(AppPreferencesContext);
  const { showTerms, isTermsReminderTimePassed } = useTermsAndConditions();
  const { openModal } = useContext(ModalContext);

  const {
    authState: { context, username },
    logout,
  } = useContext(AuthContext);

  const { push } = useHistory();
  const { shouldDisplayEOY, allowEndOfYear, eoyPath } = useDisplayEOY();
  const [showViewAsBar, setShowViewAsBar] = useState(true);

  const {
    appContext: { appClass, layoutClass, isFiltersToggleOpen },
  } = useContext(GeneralAppContext);

  const isAnalystRunsAllowed =
    useAI && isAllowed(userActionsEnum.VIEW_SEEM_CHAT);
  const isAnalystAutomationsAllowed =
    isAnalystRunsAllowed && isAutomationsEnabled;

  useEffect(() => {
    if (!!showTerms && termsAndConditions) {
      openModal({
        title: "Terms and Conditions",
        size: "s",
        overlay: true,
        component: (
          <TermsAndConditions showReminderBtn={!isTermsReminderTimePassed} />
        ),
      });
    }
  }, [showTerms, termsAndConditions, isTermsReminderTimePassed]);

  if (!hasScopesAllowed) {
    return (
      <StaticErrorFallback
        buttons={[
          {
            label: "Back to Login Page",
            onClick: logout,
          },
        ]}
        error={{
          // StaticErrorFallback types `message` as string, but it forwards the
          // value to ConfirmationDialog which renders any ReactNode. The JSX
          // message is intentional; cast until the upstream prop type is widened.
          message: (
            <div>
              <div>
                You do not have the necessary permissions to access the system.
              </div>
              <div>Please contact your Seemplicity administrator.</div>
            </div>
          ) as unknown as string,
        }}
      />
    );
  }

  return (
    <ChatStreamProvider>
      <div
        id="layout"
        className={`layout ${layoutClass} ${
          isFiltersToggleOpen ? "filters-toggle-open" : "filters-toggle-closed"
        }${isRemediationItemsSplit ? " has-grouped-header" : ""}`}
      >
        {context?.id && showViewAsBar && (
          <div
            className={`logged-with-context ${
              showDomainLocation ? "with-domain" : ""
            }`}
          >
            <IconButton
              data-testid={"viewas"}
              size={"large"}
              isIcon="seem-icon-eye"
              type={`button`}
              onClick={() => setShowViewAsBar(!showViewAsBar)}
              withTooltip={{ content: "Hide Bar" }}
              className={"logged-with-context-button"}
            />
            <Tooltip content={`Viewing ${context.customerName} as ${username}`}>
              <div className="logged-with-context-text">
                Viewing {context.customerName} - DB:{" "}
                {useClickhouse ? "ClickHouse" : "Postgres"} as {username}
              </div>
            </Tooltip>
          </div>
        )}
        {context?.mainDomain && showDomainLocation && (
          <DomainAlert domainName={context?.mainDomain} />
        )}
        {!shouldDisplayEOY ? (
          <>
            <AppHeader />
            <div id="app" className={`App ${appClass}`}>
              <DatasourcesProvider>
                <Suspense fallback={<div></div>}>
                  <Switch>
                    <ProtectedRoute path={FINDINGS_MEMBER_ROUTE_PATH}>
                      {isRemediationItemsSplit ? (
                        <RedirectPreservingParams
                          to={SEEMPLICITY_FINDINGS_ROUTE_PATH}
                        />
                      ) : (
                        <FindingsProvider />
                      )}
                    </ProtectedRoute>
                    <ProtectedRoute path={REMEDIATION_ITEMS_MEMBER_ROUTE_PATH}>
                      <RedirectPreservingParams
                        to={FINDINGS_MEMBER_ROUTE_PATH}
                      />
                    </ProtectedRoute>
                    <ProtectedRoute path={FINDINGS_ROUTE_PATH}>
                      {isRemediationItemsSplit ? (
                        <RedirectPreservingParams
                          to={REMEDIATION_ITEMS_ROUTE_PATH}
                        />
                      ) : (
                        <RemediationItemsProvider />
                      )}
                    </ProtectedRoute>
                    <ProtectedRoute path={REMEDIATION_ITEMS_ROUTE_PATH}>
                      {isRemediationItemsSplit ? (
                        <RemediationItemsProvider />
                      ) : (
                        <RedirectPreservingParams to={FINDINGS_ROUTE_PATH} />
                      )}
                    </ProtectedRoute>
                    <ProtectedRoute
                      path={`/discovery/findings-validation/:id?/:state?`}
                    >
                      <DataValidationFindingsProvider />
                    </ProtectedRoute>
                    <ProtectedRoute
                      path={[
                        `/remediationQueue/:queueid?/:id?`,
                        `/remediation`,
                      ]}
                    >
                      <AutomationsRouter />
                    </ProtectedRoute>
                    <ProtectedRoute path={`/dashboard/id_int/:id_int?`}>
                      <DashboardProvider />
                    </ProtectedRoute>
                    <ProtectedRoute path={`/dashboard/:id?/:name?`}>
                      <DashboardProvider />
                    </ProtectedRoute>
                    <ProtectedRoute path={`/policies/findings-rules/:id?`}>
                      {isAllowed(userActionsEnum.VIEW_RULES_PAGE) && <Rules />}
                    </ProtectedRoute>
                    <ProtectedRoute path={"/settings/:groupPath?/:itemPath?"}>
                      <SettingsMenu />
                    </ProtectedRoute>
                    <ProtectedRoute path={`${SILVER_RESOURCE_BASE_PATH}/:id`}>
                      <SilverResourceDetailsPaneProvider />
                    </ProtectedRoute>
                    <ProtectedRoute path={"/resources/:tabPath?/:id?"}>
                      <ResourcesProvider />
                    </ProtectedRoute>
                    <ProtectedRoute path={`/customerProvisioning`}>
                      {customerProvisioning && <CustomerProvisioning />}
                    </ProtectedRoute>
                    <ProtectedRoute path={`/systemHistory`}>
                      {isAllowed(userActionsEnum.VIEW_UNSCOPED_RESOURCES) && ( // we use the VIEW_UNSCOPED_RESOURCES until the relevant user actions are available
                        <SystemHistory />
                      )}
                    </ProtectedRoute>
                    <ProtectedRoute path={`/downloadManager`}>
                      <ReportsAuditLog />
                    </ProtectedRoute>
                    <ProtectedRoute path={"/scopes-generator"}>
                      <ScopesGeneratorPageProvider>
                        <ScopesGeneratorPage />
                      </ScopesGeneratorPageProvider>
                    </ProtectedRoute>
                    <ProtectedRoute path={`/seema/:chatId?`}>
                      {useAI && <ChatProvider />}
                    </ProtectedRoute>
                    <ProtectedRoute path={ANALYST_AUTOMATION_WIZARD_PATHS}>
                      {isAnalystAutomationsAllowed && (
                        <AnalystAutomationWizard />
                      )}
                    </ProtectedRoute>
                    <ProtectedRoute path={ANALYST_RUNS_PATHS}>
                      {isAnalystRunsAllowed && <AnalystRuns />}
                    </ProtectedRoute>
                    {!hideAggregationRulesPage && (
                      <ProtectedRoute path={"/policies/aggregation-rules"}>
                        <AggregationRulesPage />
                      </ProtectedRoute>
                    )}
                    <ProtectedRoute path={"/policies/ticket-sla-rules"}>
                      <TicketSLARulesPage />
                    </ProtectedRoute>
                    {isRemediationItemsSplit && (
                      <ProtectedRoute path={SEEMPLICITY_FINDINGS_ROUTE_PATH}>
                        <SeemplicityFindingsProvider />
                      </ProtectedRoute>
                    )}
                    {!isRemediationItemsSplit && (
                      <ProtectedRoute
                        path={SEEMPLICITY_FINDING_DETAILS_ROUTE_PATH}
                      >
                        <RedirectPreservingParams
                          to={FINDINGS_MEMBER_ROUTE_PATH}
                        />
                      </ProtectedRoute>
                    )}
                    {/* Redirect old routes to new ones */}
                    <Redirect
                      exact
                      from="/rules/aggregation"
                      to="/policies/aggregation-rules"
                    />
                    <Redirect
                      exact
                      from="/rules/:id"
                      to="/policies/findings-rules/:id"
                    />
                    <Redirect
                      exact
                      from="/rules"
                      to="/policies/findings-rules"
                    />
                    <Redirect
                      exact
                      from="/rules/ticket-sla"
                      to="/policies/ticket-sla-rules"
                    />
                    {/* temp - until Seema is adopted */}
                    <Redirect exact from="/labs/seema" to="/seema" />
                    <Redirect
                      exact
                      from="/labs/seema/:chatId"
                      to="/seema/:chatId"
                    />
                    <ProtectedRoute path={"*"}>
                      <PageNotFound
                        title={"Page Not Found"}
                        subtitle={
                          "Oops! The page you're looking for doesn't seem to exist."
                        }
                        onReachOutButtonClick={openFreshchat}
                        description={`It might have been moved, deleted, or you could've hit a wrong link.
    But don't worry, we'll help you get back on track.`}
                        callToAction={
                          <TextButton
                            label={"Back to Dashboard"}
                            buttonStyle={"primary"}
                            size={"large"}
                            onClick={() => push("/dashboard")}
                          />
                        }
                      />
                    </ProtectedRoute>
                  </Switch>
                </Suspense>
              </DatasourcesProvider>
            </div>
          </>
        ) : (
          <Switch>
            {!!allowEndOfYear && (
              <ProtectedRoute path={`/${eoyPath}`}>
                <EndOfYear />
              </ProtectedRoute>
            )}
            <ProtectedRoute path={"*"}>
              <PageNotFound
                title={"Page Not Found"}
                subtitle={
                  "Oops! The page you're looking for doesn't seem to exist."
                }
                onReachOutButtonClick={openFreshchat}
                description={`It might have been moved, deleted, or you could've hit a wrong link.
    But don't worry, we'll help you get back on track.`}
                callToAction={
                  <TextButton
                    label={"Back to Dashboard"}
                    buttonStyle={"primary"}
                    size={"large"}
                    onClick={() => push("/dashboard")}
                  />
                }
              />
            </ProtectedRoute>
          </Switch>
        )}
        <ToastRoot />
        <div id={MODAL_ROOT_ID} />
      </div>
    </ChatStreamProvider>
  );
}

export default App;
